// A plan's top-up packs and what it does when a boat runs out of tokens: set per plan here,
// never in code (Tony, 30 Oct 2026; docs/planning/TOKEN_LEDGER_PLAN.md 10.3, 10.10).
// Prices are typed in dollars and kept in cents. Nothing is sold from here yet: the
// customer's "Buy tokens" comes with the top-ups PR.

import type { ReactNode } from 'react';

export const PACK_CURRENCIES = ['NZD', 'USD', 'EUR', 'GBP', 'AUD'] as const;
type Currency = (typeof PACK_CURRENCIES)[number];

export interface TokenPack {
  id: string;
  tokens: number;
  prices: Partial<Record<Currency, number>>;
  on_sale: boolean;
}

export type OutOfTokensPolicy = 'stop' | 'overdraft' | 'grace' | 'live_keeps_drawing';

export interface TokenSettings {
  token_packs: TokenPack[];
  out_of_tokens_policy: OutOfTokensPolicy | null;
  out_of_tokens_amount: number | null;
}

export const POLICIES: Array<{ value: OutOfTokensPolicy | ''; label: string; meaning: string }> = [
  { value: '', label: 'Not set', meaning: 'As today: a download is never refused' },
  { value: 'stop', label: 'Stop', meaning: 'No new downloads or charged live tiles until topped up' },
  { value: 'overdraft', label: 'Overdraft', meaning: 'Carries on up to a limit, taken from the next allowance or top-up' },
  { value: 'grace', label: 'Grace period', meaning: 'Carries on for a few days, to top up back in signal' },
  { value: 'live_keeps_drawing', label: 'Live keeps drawing', meaning: 'Live charts carry on; only new downloads stop' },
];

const field = 'w-full px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none';

function nextId(packs: TokenPack[]): string {
  for (let n = packs.length + 1; ; n++) {
    const id = `pack-${n}`;
    if (!packs.some((p) => p.id === id)) return id;
  }
}

export function PackageTokenSettings({ value, onChange, packsInfo, runOutInfo }: {
  value: TokenSettings;
  onChange: (next: TokenSettings) => void;
  /** Optional ⓘ shown beside each heading by the plan editor. */
  packsInfo?: ReactNode;
  runOutInfo?: ReactNode;
}) {
  const packs = value.token_packs;
  const setPacks = (token_packs: TokenPack[]) => onChange({ ...value, token_packs });
  const setPack = (i: number, pack: TokenPack) => setPacks(packs.map((p, j) => (j === i ? pack : p)));
  const policy = POLICIES.find((p) => p.value === (value.out_of_tokens_policy ?? '')) ?? POLICIES[0];
  const amountLabel = value.out_of_tokens_policy === 'overdraft' ? 'Overdraft tokens'
    : value.out_of_tokens_policy === 'grace' ? 'Grace days' : null;

  return (
    <div className="space-y-6">
      <section>
        <div className="flex items-center gap-2 mb-1">
          <h3 className="text-base font-medium text-white">Top-up packs</h3>
          {packsInfo}
        </div>
        <p className="text-sm text-slate-400 mb-3">The packs customers on this plan can buy. Leave a currency empty to not sell in it.</p>
        <div className="space-y-3">
          {packs.map((pack, i) => (
            <div key={pack.id} role="group" aria-label={`Pack ${pack.id}`} className="p-3 border border-slate-700 rounded-lg space-y-3">
              <div className="flex flex-wrap items-end gap-3">
                <label className="text-sm text-slate-300">
                  Tokens
                  <input type="number" min={1} className={field} value={pack.tokens}
                    onChange={(e) => setPack(i, { ...pack, tokens: parseInt(e.target.value) || 0 })} />
                </label>
                <label className="text-sm text-slate-300 flex items-center gap-2">
                  <input type="checkbox" checked={pack.on_sale}
                    onChange={(e) => setPack(i, { ...pack, on_sale: e.target.checked })} />
                  On sale
                </label>
                <button type="button" className="ml-auto text-sm text-red-400 hover:text-red-300"
                  aria-label={`Remove pack ${pack.id}`} onClick={() => setPacks(packs.filter((_, j) => j !== i))}>
                  Remove
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {PACK_CURRENCIES.map((cur) => (
                  <label key={cur} className="text-sm text-slate-300">
                    {cur}
                    <input type="number" min={0} step="0.01" className={field}
                      value={pack.prices[cur] != null ? pack.prices[cur]! / 100 : ''}
                      onChange={(e) => {
                        const prices = { ...pack.prices };
                        const dollars = parseFloat(e.target.value);
                        if (Number.isFinite(dollars) && dollars > 0) prices[cur] = Math.round(dollars * 100);
                        else delete prices[cur];
                        setPack(i, { ...pack, prices });
                      }} />
                  </label>
                ))}
              </div>
            </div>
          ))}
          <button type="button" className="text-sm text-cyan-400 hover:text-cyan-300"
            onClick={() => setPacks([...packs, { id: nextId(packs), tokens: 10000, prices: {}, on_sale: false }])}>
            + Add a pack
          </button>
        </div>
      </section>

      <section>
        <div className="flex items-center gap-2 mb-1">
          <label htmlFor="out-of-tokens-policy" className="block text-base font-medium text-white">When this plan runs out</label>
          {runOutInfo}
        </div>
        <select id="out-of-tokens-policy" className={field} value={value.out_of_tokens_policy ?? ''}
          onChange={(e) => onChange({ ...value, out_of_tokens_policy: (e.target.value || null) as OutOfTokensPolicy | null, out_of_tokens_amount: null })}>
          {POLICIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        <p className="text-sm text-slate-400 mt-1">{policy.meaning}. Whatever is chosen, charts already on the boat always draw, and the skipper sees this rule.</p>
        {amountLabel && (
          <label className="block text-sm text-slate-300 mt-3">
            {amountLabel}
            <input type="number" min={1} className={field} value={value.out_of_tokens_amount ?? ''}
              onChange={(e) => onChange({ ...value, out_of_tokens_amount: parseInt(e.target.value) || null })} />
          </label>
        )}
      </section>
    </div>
  );
}
